/**
 * Registration Success Page
 *
 * Shown after successful registration with next steps guidance.
 * Different content for individual, broker, and company users.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ArrowRight, Users, Building2, FileText, Home } from 'lucide-react';
import confetti from 'canvas-confetti';

import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import AvatarUpload from '../../../components/profile/AvatarUpload';
import { useAuth } from '../../../hooks/useAuth';

// The welcome screen is a one-time page. React Router gives every history entry
// a stable `location.key` that survives Back/Forward, so remembering the keys we
// have already shown lets us tell a fresh arrival (straight from sign-up) from
// the user pressing Back into it later. sessionStorage is per-tab, which is the
// same scope as the history stack itself.
const WELCOME_SEEN_KEY = 's24:welcome-seen';

function readSeenWelcomes(): string[] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(WELCOME_SEEN_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function markWelcomeSeen(key: string) {
  try {
    const seen = readSeenWelcomes().filter((k) => k !== key);
    sessionStorage.setItem(WELCOME_SEEN_KEY, JSON.stringify([...seen.slice(-19), key]));
  } catch {
    // Storage blocked (private mode etc.): the page just behaves as before.
  }
}

export default function RegistrationSuccessPage() {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { session, loading: authLoading } = useAuth();

  const userType = searchParams.get('type') || 'company';
  // 'owner' comes from the intent sign-up ("I own a property"); it is a
  // personal account, so it gets the individual screen, not the business one.
  const isPersonal = userType === 'individual' || userType === 'owner';

  // Back-button guard. Read once, before anything renders. The "default" key
  // is the first entry of a fresh page load (e.g. returning from the Moyasar
  // payment page), which is always a real arrival, never a revisit.
  const [isRevisit] = useState(
    () => location.key !== 'default' && readSeenWelcomes().includes(location.key)
  );
  const bounced = useRef(false);

  useEffect(() => {
    if (!isRevisit) {
      markWelcomeSeen(location.key);
      return;
    }
    // Pressed Back into a welcome screen already shown: step over it to the
    // page before sign-up. With no earlier entry in this tab, go home instead.
    // The ref keeps StrictMode's double effect from stepping back twice.
    if (bounced.current) return;
    bounced.current = true;
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate('/', { replace: true });
  }, [isRevisit, location.key, navigate]);

  // Two paths lead here: (a) the in-wizard Confirm & Submit handler, which
  // calls activateSession() right before navigating — the session is in
  // memory by the time we mount; (b) a Moyasar return-URL redirect after
  // payment — the user lands here cold without an activated session. In
  // case (b) the AvatarUpload widget would render "You must be signed in"
  // on every action, so we hide it entirely and surface a sign-in CTA.
  const sessionReady = Boolean(session?.token) && !authLoading;

  const [showConfetti, setShowConfetti] = useState(!isRevisit);

  // Trigger confetti on mount
  useEffect(() => {
    if (showConfetti) {
      const duration = 2000;
      const end = Date.now() + duration;

      const colors = ['#004D40', '#3C9F8F', '#B7DDD7'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();

      setShowConfetti(false);
    }
  }, [showConfetti]);

  if (isRevisit) return null;

  // The links below REPLACE this history entry rather than pushing a new one,
  // so leaving the welcome screen takes it out of the Back stack: Back from the
  // next page returns to where the user was before signing up.

  // Individual success content
  if (isPersonal) {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />

        <div className="pt-24 pb-12 px-4">
          <div className="max-w-lg mx-auto text-center" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>

            <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
              {t('individualSuccess.title')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              {t('individualSuccess.subtitle')}
            </p>

            {/* Next Steps */}
            <div className={`bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-8 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h2 className="font-medium text-blue-900 dark:text-blue-200 mb-4">
                {t('individualSuccess.nextSteps')}
              </h2>
              <ol className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0 font-medium">
                    1
                  </span>
                  {t('individualSuccess.step1')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0 font-medium">
                    2
                  </span>
                  {t('individualSuccess.step2')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0 font-medium">
                    3
                  </span>
                  {t('individualSuccess.step3')}
                </li>
              </ol>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/fal-disclaimer" replace>
                <Button>
                  <FileText className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('individualSuccess.listProperty')}
                </Button>
              </Link>
              <Link to="/" replace>
                <Button variant="outline">
                  <Home className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('individualSuccess.goHome')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Broker/Company success content
  return (
    <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-lg mx-auto text-center" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>

          <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
            {t('success.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            {t('success.subtitle')}
          </p>

          {/* Status Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('success.accountCreated', 'Account Created')}
            </div>
            {userType === 'company' && (
              <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm">
                <CheckCircle className="w-4 h-4" />
                {t('success.companyCreated')}
              </div>
            )}
            {userType === 'broker' && (
              <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm">
                <CheckCircle className="w-4 h-4" />
                {t('success.profileCreated', 'Profile Created')}
              </div>
            )}
          </div>

          {/* Optional: profile picture. The user can skip and add later from
              /profile or Settings — the AvatarUpload widget shows the current
              avatar (initials fallback) and writes to User.avatarUrl.
              Only rendered when we actually have an active session (see
              sessionReady comment above) — otherwise the upload would fail. */}
          {sessionReady ? (
            <div className={`bg-white dark:bg-gray-800 rounded-xl border p-6 mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h2 className="font-medium text-[var(--pv-color-primary)] dark:text-white mb-1">
                {isRTL ? 'أضف صورة الملف الشخصي (اختياري)' : 'Add a profile picture (optional)'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {isRTL
                  ? 'يمكنك تخطّي هذه الخطوة وإضافة الصورة لاحقاً من إعدادات الحساب'
                  : 'You can skip this and add it later from your account settings'}
              </p>
              <AvatarUpload />
            </div>
          ) : (
            <div className={`bg-white dark:bg-gray-800 rounded-xl border p-6 mb-6 text-center`}>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {isRTL
                  ? 'سجّل الدخول لتفعيل حسابك وإضافة صورة الملف الشخصي.'
                  : 'Sign in to activate your account and add a profile picture.'}
              </p>
              <Link to="/signin?redirect=/account" replace>
                <Button variant="outline" size="sm">
                  {isRTL ? 'تسجيل الدخول' : 'Sign in'}
                </Button>
              </Link>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 mb-8">
            <h2 className="font-medium text-[var(--pv-color-primary)] dark:text-white mb-4">
              {t('success.whatNext')}
            </h2>

            <div className="space-y-4">
              <Link
                to={userType === 'company' ? '/company/settings' : '/profile'}
                replace
                className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#004D40]" />
                  </div>
                  <span className="font-medium">{t('success.completeProfile')}</span>
                </div>
                <ArrowRight className={`w-5 h-5 text-gray-400 group-hover:text-[#004D40] transition-colors ${isRTL ? 'rotate-180' : ''}`} />
              </Link>

              {userType === 'company' && (
                <Link
                  to="/company/team"
                  replace
                  className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/10 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-medium">{t('success.inviteTeam')}</span>
                  </div>
                  <ArrowRight className={`w-5 h-5 text-gray-400 group-hover:text-[#004D40] transition-colors ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
              )}

              <Link
                to="/properties/new"
                replace
                className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-medium">{t('success.addListing')}</span>
                </div>
                <ArrowRight className={`w-5 h-5 text-gray-400 group-hover:text-[#004D40] transition-colors ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          </div>

          {/* Go to Dashboard */}
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => {
                const dashboardPath = userType === 'broker' ? '/dashboard' : '/company/dashboard';
                navigate(dashboardPath, { replace: true });
              }}
            >
              {isRTL ? 'الذهاب للوحة التحكم' : 'Go to Dashboard'}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
